import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import * as conversationsApi from '@/api/conversations.api';
import * as supervisionApi from '@/api/supervision.api';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';

/**
 * PASAR UN CHAT A OTRA ASESORA — de quien supervisa (administración o encargado).
 *
 * El reparto le da cada chat a una asesora; cuando esa asesora no está, o el cliente pide a
 * otra, quien supervisa lo mueve. Se podía por la API y no había botón. La lista sale de
 * Supervisión (las que reciben chats hoy, con cuántos tiene abiertos cada una) para pasárselo a
 * quien esté más libre.
 */
export function PasarChat({
  conversationId,
  cliente,
  asignadaA,
}: {
  conversationId: string;
  cliente: string;
  asignadaA: { id: string; nombre: string } | null;
}) {
  const [abierto, setAbierto] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['supervision', 'hoy'],
    queryFn: () => supervisionApi.supervision('hoy'),
    enabled: abierto,
  });
  const asesoras = (data?.filas ?? []).filter((f) => f.userId && f.recibeChats);

  const pasar = useMutation({
    mutationFn: (a: { userId: string; nombre: string }) => conversationsApi.assignUser(conversationId, a.userId),
    onSuccess: (_r, a) => {
      toast.success(`Chat pasado a ${a.nombre}.`);
      setAbierto(false);
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo pasar el chat'),
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setAbierto(true)}
        title={asignadaA ? `Lo atiende ${asignadaA.nombre}. Pasarlo a otra asesora` : 'Pasar este chat a una asesora'}
      >
        <ArrowRightLeft className="h-4 w-4" /> Pasar chat
      </Button>
      <Modal
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Pasar el chat a otra asesora"
        description={`${cliente} · ${asignadaA ? `lo atiende ${asignadaA.nombre}` : 'sin asesora asignada'}`}
      >
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar la lista de asesoras.</p>
        ) : asesoras.length === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Todavía no hay asesoras que reciban chats. Sus cuentas las crea la administración (rol «Agente»).
          </p>
        ) : (
          <ul className="space-y-2">
            {asesoras.map((a) => {
              const esLaActual = a.userId === asignadaA?.id;
              return (
                <li key={a.userId}>
                  <button
                    type="button"
                    disabled={esLaActual || pasar.isPending}
                    onClick={() => pasar.mutate({ userId: a.userId!, nombre: a.nombre })}
                    className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border border-surface-border px-4 py-2 text-left text-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-800"
                  >
                    <span className="font-medium text-slate-900 dark:text-slate-100">{a.nombre}</span>
                    <span className="text-xs text-slate-500">
                      {esLaActual
                        ? 'ya lo tiene'
                        : `${a.chatsAbiertos} abierto${a.chatsAbiertos === 1 ? '' : 's'}${a.sinContestar ? ` · ${a.sinContestar} sin contestar` : ''}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>
    </>
  );
}
